import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, getInitialLanguage } from "@/context/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import ReviewerDashboard from "@/pages/ReviewerDashboard";
import CaseForm from "@/pages/CaseForm";
import NotificationsPage from "@/pages/NotificationsPage";
import Profile from "@/pages/Profile";

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#0A2540] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "reviewer") return <Navigate to="/reviewer" replace />;
  return <Navigate to="/agent" replace />;
};

const AnyRoleCase = () => (
  <ProtectedRoute roles={["agent", "reviewer", "admin"]}>
    <CaseForm />
  </ProtectedRoute>
);

const AnyAuthed = ({ children }) => (
  <ProtectedRoute roles={["agent", "reviewer", "admin"]}>{children}</ProtectedRoute>
);

const AppRouter = () => {
  const location = useLocation();
  if (location.pathname === "/" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent"
        element={
          <ProtectedRoute roles={["agent"]}>
            <AgentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/cases/new"
        element={
          <ProtectedRoute roles={["agent"]}>
            <CaseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/cases/:id/edit"
        element={
          <ProtectedRoute roles={["agent"]}>
            <CaseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/cases/:id"
        element={
          <ProtectedRoute roles={["agent"]}>
            <CaseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reviewer"
        element={
          <ProtectedRoute roles={["reviewer"]}>
            <ReviewerDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/cases/:id" element={<AnyRoleCase />} />
      <Route path="/reviewer/cases/:id" element={<AnyRoleCase />} />
      <Route path="/admin/cases/:id" element={<AnyRoleCase />} />
      <Route
        path="/notifications"
        element={
          <AnyAuthed>
            <NotificationsPage />
          </AnyAuthed>
        }
      />
      <Route
        path="/profile"
        element={
          <AnyAuthed>
            <Profile />
          </AnyAuthed>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider initial={getInitialLanguage()}>
        <BrowserRouter>
          <AuthProvider>
            <NotificationProvider>
              <AppRouter />
              <Toaster richColors position="top-right" />
            </NotificationProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
